import React, { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { InformationPopUp, FormSpacer } from 'components';
import { BreachType, FormDataType } from '../../../../contexts/FormProvider';
import { useContentContext } from '@qbe/dfc-content';
import { FormGroupLabel, Button, DatePicker } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { isEmpty } from 'lodash';
import { SEPARATOR } from 'utils/constants';
import { TextFieldController } from 'controllers';
import dayjs from 'dayjs';
import { useContentComplaintPartners } from 'utils/content';

interface Props {
  form: UseFormReturn<FormDataType>;
  isFieldRequired: (fieldName: keyof FormDataType) => boolean;
  show: boolean;
  isValid: boolean;
  showSubmitButton: boolean;
}

const Resolution: FC<Props> = ({ form, isFieldRequired, show = false, showSubmitButton = true, isValid = false }) => {
  const { control, watch } = form;

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const breachType = watch('breachType') as BreachType;
  const isDataPrivacy = breachType === 'Data, Privacy, Security';

  const resolved = watch('resolved');
  const isResolved = watch('resolved') === 'yes';

  if (!show) return null;

  return (
    <FormCard variant="sectionTitle" title={getTextContent('breach.tell.us.more.resolution.heading')}>
      <Controller
        name="resolved"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Toggle
            {...field}
            id={field.name}
            required
            title={getTextContent('breach.tell.us.more.resolution.isResolved')}
            data-track={field.name}
            data-test="resolved"
            data-track-value="capture"
            optionList={[
              {
                text: 'Yes',
                value: 'yes',
              },
              {
                text: 'No',
                value: 'no',
              },
            ].map(({ text, value }) => ({
              checked: value === field.value,
              id: `${field.name}${SEPARATOR}${value}`,
              label: text,
              value,
            }))}
          />
        )}
      />

      {isResolved && (
        <>
          <FormGroupLabel alignCenter required title={getTextContent('breach.tell.us.more.resolution.dateResolved')} />
          <FormSpacer type="input" />
          <Controller
            control={control}
            name="resolvedDate"
            render={({ field: { value, ...field }, fieldState: { error } }) => (
              <DatePicker
                {...field}
                id={field.name}
                value={dayjs(value as any)}
                label="DD/MM/YYYY"
                format="DD/MM/YYYY"
                data-track={field.name}
                data-track-value="mask"
                error={!!error}
                maxDate={dayjs()}
                data-test={field.name}
                helperText={error?.message}
              />
            )}
          />
        </>
      )}

      {!isEmpty(resolved) && (
        <>
          <FormGroupLabel
            required={isFieldRequired('resolutionDesc')}
            alignCenter
            title={getTextContent('breach.tell.us.more.resolution.resolutionDetails')}
            titleSuffix={
              (
                <InformationPopUp
                  message={getTextContent('breach.tell.us.more.resolution.resolutionDetails.infoText')}
                  dataTrack="infoIconAcknowledgement"
                />
              ) as any
            }
            subtitle={
              !isDataPrivacy
                ? isResolved
                  ? getTextContent('breach.tell.us.more.resolution.resolutionDetails.isResolved.helperText')
                  : getTextContent('breach.tell.us.more.resolution.resolutionDetails.isNotResolved.helperText')
                : getTextContent('breach.tell.us.more.resolution.resolutionDetails.dataPrivacy.helperText')
            }
          />
          <FormSpacer type="input" />

          <TextFieldController multiline name="resolutionDesc" control={control} label="Description" maxLength={4000} />
        </>
      )}

      {showSubmitButton && (
        <>
          <FormSpacer type="formgroup" />

          <Button type="submit" label="Submit" disabled={!isValid} />
        </>
      )}
    </FormCard>
  );
};

export default Resolution;
